import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader, Check, X, AlertCircle, Search, ArrowLeft, RefreshCw, Pencil, Trash2, Plus, Save, FileInput,ChevronRight, ChevronLeft, ChevronsRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDateTime } from '../services/utils';
import { LoadingSpinner, Toggler } from '../components/common';
import { 
  KnowledgeBaseItem, 
  NewKnowledgeBaseForm, 
  SharePointContentItem, 
  FormValidationErrors, 
  DeleteConfirmationModalProps, 
  PreviewChangesModalProps, 
  ContentDetailsModalProps, 
  SelectedContentSummaryProps, 
  SharePointBrowserModalProps, 
  SharePointConnection,
  useKnowledgeBaseApi,
  useApiRequest,
  DEFAULT_LOCALE,
  LOCALES
} from '../services/api/knowledgebaseService';

const MAX_KNOWLEDGE_BASES = 500;
// Modal Components
const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  knowledgeBaseName,
  isDeleting
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
        <div className="flex items-center space-x-3 mb-4">
          <AlertCircle className="h-6 w-6 text-red-600" />
          <h3 className="text-lg font-medium text-gray-900">Confirm Delete</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Are you sure you want to delete the knowledge base <span className="font-medium">{knowledgeBaseName}</span>?
          This action cannot be undone.
        </p>
        <p className="text-sm text-amber-600 mb-4">
          Warning: Deleting this knowledge base removes SharePoint mapping only. Please manually delete KB in Genesys Cloud.
        </p>
        <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
          <button
            type="button"
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={isDeleting}
            aria-label="Cancel deletion"
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onConfirm}
            disabled={isDeleting}
            aria-label="Confirm deletion"
          >
            {isDeleting ? (
              <>
                <Loader size={16} className="mr-2 animate-spin" />
                Deleting...
              </>
            ) : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PreviewChangesModal: React.FC<PreviewChangesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  connections,
  addedContent,
  removedContent,
  unchangedContent
}) => {
  if (!isOpen) return null;

  const getContentCount = (content: Record<string, string[]>) =>
    Object.values(content).reduce((total, paths) => total + paths.length, 0);

  const getConnectionName = (siteKey: string) => {
    const [, connectionId] = siteKey.split('||');
    const connection = connections.find((conn: any) => conn.connectionId === connectionId);
    return connection?.connectionName || siteKey;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-xl">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Preview Changes</h3>
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700">Added Content ({getContentCount(addedContent)} items)</h4>
          {Object.keys(addedContent).length ? (
            <div>
              {Object.entries(addedContent).map(([siteKey, paths]) => (
                <div key={siteKey} className="text-sm text-gray-600">
                  {getConnectionName(siteKey)} ({paths.length} items)
                  <ul className="ml-4 list-disc">
                    {paths.map((path, index) => (
                      <li key={index} className="truncate">{path}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No new content added.</p>
          )}
        </div>
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700">Removed Content ({getContentCount(removedContent)} items)</h4>
          {Object.keys(removedContent).length ? (
            <div>
              {Object.entries(removedContent).map(([siteKey, paths]) => (
                <div key={siteKey} className="text-sm text-gray-600">
                  {getConnectionName(siteKey)} ({paths.length} items)
                  <ul className="ml-4 list-disc">
                    {paths.map((path, index) => (
                      <li key={index} className="truncate">{path}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No content removed.</p>
          )}
        </div>
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700">Unchanged Content ({getContentCount(unchangedContent)} items)</h4>
          {Object.keys(unchangedContent).length ? (
            <div>
              {Object.entries(unchangedContent).map(([siteKey, paths]) => (
                <div key={siteKey} className="text-sm text-gray-600">
                  {getConnectionName(siteKey)} ({paths.length} items)
                  <ul className="ml-4 list-disc">
                    {paths.map((path, index) => (
                      <li key={index} className="truncate">{path}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No unchanged content.</p>
          )}
        </div>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={onClose}
            aria-label="Cancel preview"
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={onConfirm}
            aria-label="Confirm changes"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const ContentDetailsModal: React.FC<ContentDetailsModalProps> = ({
  isOpen,
  onClose,
  sitePath,
  connectionName,
  connectionId,
  contentPaths,
}) => {

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-xl">
        <div className="flex items-center space-x-3 mb-4">
          <Search className="h-6 w-6 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">
            Content Details for {connectionName}
          </h3>
        </div>
        <div className="max-h-96 overflow-y-auto mb-4">
          {contentPaths.length > 0 ? (
            <ul className="text-sm text-gray-600 space-y-2">
              {contentPaths.map((path, index) => (
                <li key={index} className="truncate">
                  {path}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No content available for this connection.</p>
          )}
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={onClose}
            aria-label="Close content details"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const SharePointBrowserModal: React.FC<SharePointBrowserModalProps> = ({
  isOpen,
  onClose,
  connections,
  selectedContentBySite,
  onContentSelect
}) => {
  const { executeRequest } = useApiRequest();
  const { fetchSharePointContent } = useKnowledgeBaseApi();

  const [currentSite, setCurrentSite] = useState<string>('am');
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [content, setContent] = useState<SharePointContentItem[]>([]);
  const [contentLoading, setContentLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pathHistory, setPathHistory] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [paginationState, setPaginationState] = useState<{
    hasMore: boolean;
    totalItems: number;
    nextUrl: string | null;
  }>({ hasMore: false, totalItems: 0, nextUrl: null });

  const sites = useMemo(() => {
    return connections.map((conn) => ({
      id: conn.connectionId,
      url: conn.siteName,
      name: conn.connectionName || conn.siteName,
      namesite: {
        url: conn.siteName,
        name: conn.connectionName || conn.siteName,
        connectionId: conn.connectionId,
        spTenantId: conn.spTenantId,
      },
    }));
  }, [connections]);

  const getSiteDetails = useCallback((siteData: string): { url: string; name: string; connectionId: string, spTenantId: string } => {
    if (!siteData) return { url: '', name: 'Unknown Connection', connectionId: '', spTenantId: '' };
    try {
      const site = JSON.parse(siteData);
      return { 
        url: site.url || '', 
        name: site.name || 'Unknown Connection', 
        connectionId: site.connectionId || '',
        spTenantId: site.spTenantId || '',
      };
    } catch (error) {
      console.error('Error parsing site data', error);
      return { url: '', name: 'Unknown Connection', connectionId: '', spTenantId: '' };
    }
  }, []);

  const getCompositeKey = useCallback((siteUrl: string, connectionId: string): string => {
    return `${siteUrl}||${connectionId}`;
  }, []);

  const handleSiteChange = useCallback(async (siteData: string) => {
    if (!siteData) {
      setCurrentSite('');
      setCurrentPath('/');
      setPathHistory([]);
      setCurrentPage(1);
      setContent([]);
      setPaginationState({ hasMore: false, totalItems: 0, nextUrl: null });
      return;
    }

    const { url: siteUrl, name: connectionName, connectionId, spTenantId } = getSiteDetails(siteData);
    if (!siteUrl || !connectionId) return;

    // Set loading state and reset content-related states

    setContentLoading(true);
    try {
      // Perform all state updates after the API call to avoid triggering useEffect prematurely
      const fileInclusions = connections.find(
        (con) => con.siteName === siteUrl && con.connectionId === connectionId
      )?.fileInclusions || [];

      const data = await executeRequest(
        () => fetchSharePointContent(siteUrl, '/', fileInclusions, spTenantId),
        undefined,
        (error) => {
          toast.error(`Error loading content: ${error}`);
        }
      );

      if (data) {
        setCurrentSite(siteData);
        setCurrentPath('/');
        setPathHistory([]);
        setCurrentPage(1);
        setContent(data.items.map((item: SharePointContentItem) => ({ ...item, connectionName, connectionId })));
        setPaginationState({
          hasMore: data.pagination.hasMore,
          totalItems: data.pagination.totalItems,
          nextUrl: data.pagination.nextUrl,
        });
      } else {
        setCurrentSite('');
        setCurrentPath('/');
        setPathHistory([]);
        setCurrentPage(1);
        setContent([]);
        setPaginationState({ hasMore: false, totalItems: 0, nextUrl: null });
      }
    } catch (error) {
      console.error('Error in handleSiteChange', error);
      toast.error('Failed to load SharePoint content');
      setCurrentSite('');
      setCurrentPath('/');
      setPathHistory([]);
      setCurrentPage(1);
      setContent([]);
      setPaginationState({ hasMore: false, totalItems: 0, nextUrl: null });
    } finally {
      setContentLoading(false);
    }
  }, [connections, executeRequest, fetchSharePointContent, getSiteDetails]);

  const loadFolder = useCallback(async (item: SharePointContentItem) => {
    if (item.type !== 'folder') return;

    const { sitePath: siteUrl, connectionName, connectionId, spTenantId } = item;
    if (!siteUrl || !connectionName || !connectionId) return;

    setContentLoading(true);
    try {
      const fileInclusions = connections.find(con => con.siteName === siteUrl && con.connectionId === connectionId)?.fileInclusions || [];
      let itemPath = item.customPath;

      const parts = item.customPath.split('/');
      if (parts.length >= 3 && parts[1] === 'Shared Documents') {
        parts[1] = 'Documents';
        itemPath = parts.join('/');
      }

      const data = await executeRequest(
        () => fetchSharePointContent(siteUrl, itemPath, fileInclusions, spTenantId),
        undefined,
        (error) => {
          toast.error(`Error loading folder content: ${error.message}`);
        }
      );

      if (data) {
        setPathHistory(prev => [...prev, currentPath]);
        setCurrentPath(itemPath);
        setCurrentPage(1);
        setContent(data.items.map((item: SharePointContentItem) => ({ ...item, connectionName, connectionId })));
        setPaginationState({
          hasMore: data.pagination.hasMore,
          totalItems: data.pagination.totalItems,
          nextUrl: data.pagination.nextUrl,
        });
      } else {
        setContent([]);
        setPaginationState({ hasMore: false, totalItems: 0, nextUrl: null });
      }
    } catch (error) {
      console.error('Error in loadFolder', error);
      toast.error('Failed to load folder content');
      setContent([]);
      setPaginationState({ hasMore: false, totalItems: 0, nextUrl: null });
    } finally {
      setContentLoading(false);
    }
  }, [currentPath, connections, executeRequest, fetchSharePointContent]);

  const fetchNextPage = useCallback(async () => {
    if (!paginationState.nextUrl || !paginationState.hasMore || contentLoading) return;

    const { url: siteUrl, name: connectionName, connectionId, spTenantId } = getSiteDetails(currentSite);
    if (!siteUrl || !connectionId) return;

    setContentLoading(true);
    try {
      const fileInclusions = connections.find(con => con.siteName === siteUrl && con.connectionId === connectionId)?.fileInclusions || [];

      const data = await executeRequest(
        () => fetchSharePointContent( siteUrl, currentPath, fileInclusions, spTenantId, paginationState.nextUrl !== null ? paginationState.nextUrl : undefined),
        undefined,
        (error) => {
          toast.error(`Error fetching next page: ${error.message}`);
        }
      );

      if (data) {
        setContent((prev) => [
          ...prev,
          ...data.items.map((item: SharePointContentItem) => ({ ...item, connectionName, connectionId }))
        ]);
        setPaginationState({
          hasMore: data.pagination.hasMore,
          totalItems: data.pagination.totalItems,
          nextUrl: data.pagination.nextUrl,
        });
      }
    } catch (error) {
      console.error('Error in fetchNextPage', error);
      toast.error('Failed to fetch next page content');
    } finally {
      setContentLoading(false);
    }
  }, [paginationState, currentSite, currentPath, connections, executeRequest, fetchSharePointContent, getSiteDetails, contentLoading]);

  // useEffect(() => {
  //   if (!currentSite || contentLoading || !paginationState.hasMore) return;

  //   const totalPages = Math.ceil(paginationState.totalItems / itemsPerPage) || 1;
  //   if (currentPage === totalPages - 1) {
  //     fetchNextPage();
  //   }
  // }, [currentPage, paginationState, itemsPerPage, currentSite, contentLoading, fetchNextPage]);

  const goBack = useCallback(async () => {
    if (pathHistory.length === 0) return;

    const { url: siteUrl, name: connectionName, connectionId, spTenantId } = getSiteDetails(currentSite);
    if (!siteUrl || !connectionName || !connectionId) return;

    const previousPath = pathHistory[pathHistory.length - 1];

    setContentLoading(true);
    try {
      const fileInclusions = connections.find(con => con.siteName === siteUrl && con.connectionId === connectionId)?.fileInclusions || [];

      const data = await executeRequest(
        () => fetchSharePointContent(siteUrl, previousPath, fileInclusions, spTenantId),
        undefined,
        (error) => {
          toast.error(`Error navigating back: ${error.message}`);
        }
      );

      if (data) {
        setPathHistory(prev => prev.slice(0, -1));
        setCurrentPath(previousPath);
        setCurrentPage(1);
        setContent(data.items.map((item: SharePointContentItem) => ({ ...item, connectionName, connectionId })));
        setPaginationState({
          hasMore: data.pagination.hasMore,
          totalItems: data.pagination.totalItems,
          nextUrl: data.pagination.nextUrl,
        });
      } else {
        setContent([]);
        setPaginationState({ hasMore: false, totalItems: 0, nextUrl: null });
      }
    } catch (error) {
      console.error('Error in goBack', error);
      toast.error('Failed to navigate back');
      setContent([]);
      setPaginationState({ hasMore: false, totalItems: 0, nextUrl: null });
    } finally {
      setContentLoading(false);
    }
  }, [pathHistory, currentSite, connections, executeRequest, fetchSharePointContent, getSiteDetails]);

  const toggleContentSelection = useCallback(
    (item: SharePointContentItem) => {
      const compositeKey = getCompositeKey(item.sitePath, item.connectionId);
      onContentSelect(compositeKey, item.customPath);
    },
    [onContentSelect, getCompositeKey]
  );

  const parseCompositeKey = (key: string): { sitePath: string; connectionName: string; connectionId: string } => {
    const [sitePath, connectionId] = key.split('||');
    const connection = connections.find((conn: any) => conn.connectionId === connectionId);
    return { 
      sitePath, 
      connectionName: connection?.connectionName || 'Unknown Connection', 
      connectionId 
    };
  };

  const isContentSelected = useCallback(
    (item: SharePointContentItem) => {
      const compositeKey = getCompositeKey(item.sitePath, item.connectionId);
      return selectedContentBySite[compositeKey]?.includes(item.customPath) || false;
    },
    [selectedContentBySite, getCompositeKey]
  );

  const filteredContent = useMemo(() => {
    if (!searchTerm) return content;
    return content.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.customPath.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [content, searchTerm]);

  const totalPages = Math.ceil(filteredContent.length / itemsPerPage) || 1;
  const paginatedContent = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredContent.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredContent, currentPage, itemsPerPage]);

  const handleItemsPerPageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }, []);

  const toggleSelectAll = useCallback(() => {
    const { url: siteUrl, connectionId } = getSiteDetails(currentSite);
    if (!siteUrl || !connectionId) return;

    const compositeKey = getCompositeKey(siteUrl, connectionId);
    const allPaths = paginatedContent.map(item => item.customPath);
    const allSelected = paginatedContent.length > 0 &&
      paginatedContent.every(item => isContentSelected(item));

    if (allSelected) {
      const newSelectedPaths = selectedContentBySite[compositeKey]?.filter(
        path => !allPaths.includes(path)
      ) || [];

      onContentSelect(compositeKey, '');
      if (newSelectedPaths.length > 0) {
        newSelectedPaths.forEach(path => {
          onContentSelect(compositeKey, path);
        });
      }
    } else {
      allPaths.forEach(path => {
        if (!selectedContentBySite[compositeKey]?.includes(path)) {
          onContentSelect(compositeKey, path);
        }
      });
    }
  }, [currentSite, paginatedContent, isContentSelected, onContentSelect, selectedContentBySite, getSiteDetails, getCompositeKey]);

  const onRemoveSite = useCallback((compositeKey: string) => {
    if (compositeKey === 'all') {
      Object.keys(selectedContentBySite).forEach(key => {
        onContentSelect(key, '');
      });
    } else {
      onContentSelect(compositeKey, '');
    }
  }, [onContentSelect, selectedContentBySite]);

  const renderBreadcrumbs = useCallback(() => {
    const { url: siteUrl, name: connectionName, connectionId, spTenantId } = getSiteDetails(currentSite);
    if (!siteUrl || !connectionName || !connectionId) return null;

    const pathParts = currentPath.split('/').filter(part => part);
    let accumulatedPath = '';
    return (
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <button
          className="text-blue-600 hover:text-blue-800"
          onClick={() => handleSiteChange(currentSite)}
          aria-label="Go to root"
          disabled={contentLoading}
        >
          Root
        </button>
        {pathParts.map((part, index) => {
          accumulatedPath += `/${part}`;
          const pathToNavigate = accumulatedPath;
          return (
            <span key={index} className="flex items-center">
              <span className="mx-1">/</span>
              <button
                className="text-blue-600 hover:text-blue-800"
                onClick={() => {
                  setCurrentPath(pathToNavigate);
                  setPathHistory(prev => prev.slice(0, index));
                  loadFolder({ type: 'folder', customPath: pathToNavigate, sitePath: siteUrl, connectionName, connectionId, spTenantId } as SharePointContentItem);
                }}
                aria-label={`Go to ${part}`}
                disabled={contentLoading}
              >
                {part}
              </button>
            </span>
          );
        })}
      </div>
    );
  }, [currentPath, currentSite, contentLoading, handleSiteChange, loadFolder, getSiteDetails]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-medium text-gray-900">SharePoint Browser</h4>
          <button
            onClick={onClose}
            className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            aria-label="Done selecting SharePoint Content"
            disabled={contentLoading}
          >
            <Check size={18} className="mr-1 text-white" />
            Done
          </button>
        </div>

        <div className="bg-gray-50 p-3 rounded-md mb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-grow">
              <label htmlFor="site-select" className="block text-sm font-medium text-gray-700 mb-1">
                Select SharePoint Site
              </label>
              <select
                id="site-select"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                value={currentSite}
                onChange={(e) => handleSiteChange(e.target.value)}
                disabled={contentLoading}
              >
                <option value="">-- Select Connection --</option>
                {sites.map((site) => (
                  <option key={site.id} value={JSON.stringify(site.namesite)}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>

            {currentSite && (
              <div className="flex-grow">
                <label htmlFor="search-content" className="block text-sm font-medium text-gray-700 mb-1">
                  Search Content
                </label>
                <div className="relative rounded-md shadow-sm">
                  <input
                    id="search-content"
                    type="text"
                    className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="Search by name or path..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={contentLoading}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <Search size={16} className="text-gray-400" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mb-4 p-4 bg-gray-50 rounded-md">
          <div className="flex justify-between items-center mb-2">
            <h5 className="text-sm font-medium text-gray-700">
              Selected Content ({Object.keys(selectedContentBySite).length} connections, {Object.values(selectedContentBySite).reduce((total, paths) => total + paths.length, 0)} items)
            </h5>
            <button
              className="text-red-600 hover:text-red-900 text-sm"
              onClick={() => onRemoveSite('all')}
              aria-label="Clear all selected content"
              disabled={contentLoading}
            >
              Clear All
            </button>
          </div>
          {Object.entries(selectedContentBySite).map(([siteKey, contentPaths]) => {
            if (contentPaths.length === 0) return null;
            const { connectionName } = parseCompositeKey(siteKey);
            return (
              <div key={siteKey} className="mb-2 last:mb-0">
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium text-gray-800">
                    {connectionName} ({contentPaths.length} items)
                  </div>
                  <button
                    className="text-gray-400 hover:text-red-500"
                    onClick={() => onRemoveSite(siteKey)}
                    aria-label={`Remove connection ${connectionName}`}
                    disabled={contentLoading}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {currentSite && (
          <div className="border border-gray-200 rounded-md overflow-hidden">
            {currentPath !== '/' && (
              <div className="bg-gray-50 px-4 py-2 flex items-center space-x-3">
                <button
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  onClick={goBack}
                  aria-label="Go back to previous folder"
                  disabled={contentLoading}
                >
                  <ArrowLeft size={16} className="mr-1" />
                  Back
                </button>
                {renderBreadcrumbs()}
              </div>
            )}

            {contentLoading ? (
              <LoadingSpinner
                title="Loading SharePoint Content"
                message="Please wait while we fetch your SharePoint data..."
                size="large"
                fullScreen={false}
                className="py-16 bg-white"
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="w-12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          onChange={toggleSelectAll}
                          checked={
                            paginatedContent.length > 0 &&
                            paginatedContent.every(item => isContentSelected(item))
                          }
                          aria-label="Select all content items"
                          disabled={contentLoading}
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Path
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedContent.length > 0 ? (
                      paginatedContent.map((item) => (
                        <tr key={item.id} className={isContentSelected(item) ? 'bg-blue-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              checked={isContentSelected(item)}
                              onChange={() => toggleContentSelection(item)}
                              aria-label={`Select ${item.name}`}
                              disabled={contentLoading}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.type === 'folder' ? (
                              <button
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                                onClick={() => loadFolder(item)}
                                aria-label={`Open folder ${item.name}`}
                                disabled={contentLoading}
                              >
                                {item.name}
                              </button>
                            ) : item.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 truncate max-w-xs">
                            {item.customPath}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                          {searchTerm ? 'No matching content found.' : 'No content available for this location.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="flex justify-between items-center px-6 py-3 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <label htmlFor="items-per-page" className="text-sm font-medium text-gray-700">
                      Items per page:
                    </label>
                    <select
                      id="items-per-page"
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      disabled={contentLoading}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1 || contentLoading}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={16} className="mr-2"/>
                    </button>
                    <span className="text-sm font-medium text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={(currentPage === totalPages && !paginationState.hasMore) || contentLoading}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Next page"
                    >
                      <ChevronRight size={16} className="mr-2"/>
                    </button>
                    <button
                      onClick={() => fetchNextPage()}
                      style = { {display : paginationState.hasMore ? 'inline' : 'none'}}
                      disabled={ contentLoading}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Next page"
                    >
                      <ChevronsRight size={16} className="mr-2"/>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SelectedContentSummary: React.FC<SelectedContentSummaryProps> = ({
  selectedContentBySite,
  onRemoveSite,
  isEditing,
  connections,
  originalContentBySite = {},
}) => {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [selectedConnectionName, setSelectedConnectionName] = useState<string | null>(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [selectedContentPaths, setSelectedContentPaths] = useState<string[]>([]);

  const getSelectedSiteCount = () => Object.keys(selectedContentBySite).length;
  const getSelectedContentCount = () =>
    Object.values(selectedContentBySite).reduce((total, paths) => total + paths.length, 0);

  const parseCompositeKey = (key: string): { sitePath: string; connectionName: string; connectionId: string } => {
    const [sitePath, connectionId] = key.split('||');
    const connection = connections.find((conn: any) => conn.connectionId === connectionId);
    return { 
      sitePath, 
      connectionName: connection?.connectionName || 'Unknown Connection', 
      connectionId 
    };
  };

  const handleOpenDetails = (siteKey: string) => {
    const { sitePath, connectionName, connectionId } = parseCompositeKey(siteKey);
    setSelectedSite(sitePath);
    setSelectedConnectionName(connectionName);
    setSelectedConnectionId(connectionId);
    setSelectedContentPaths(selectedContentBySite[siteKey] || []);
    setIsDetailsModalOpen(true);
  };

  if (getSelectedContentCount() === 0) return null;

  return (
    <div className="mb-4 p-4 bg-gray-50 rounded-md">
      <div className="flex justify-between items-center mb-2">
        <h5 className="text-sm font-medium text-gray-700">
          Selected Content ({getSelectedSiteCount()} connections, {getSelectedContentCount()} items)
        </h5>
        <button
          className="text-red-600 hover:text-red-900 text-sm"
          onClick={() => onRemoveSite('all')}
          aria-label="Clear all selected content"
        >
          Clear All
        </button>
      </div>
      {Object.entries(selectedContentBySite).map(([siteKey, contentPaths]) => {
        if (contentPaths.length === 0) return null;
        const { connectionName } = parseCompositeKey(siteKey);
        return (
          <div key={siteKey} className="mb-2 last:mb-0">
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium text-gray-800">
                {connectionName} ({contentPaths.length} items)
                {isEditing && originalContentBySite[siteKey] && (
                  <button
                    className="ml-2 text-xs text-blue-600 hover:text-blue-800 underline"
                    onClick={() => handleOpenDetails(siteKey)}
                    aria-label={`View details for ${connectionName}`}
                  >
                    (Existing)
                  </button>
                )}
              </div>
              <button
                className="text-gray-400 hover:text-red-500"
                onClick={() => onRemoveSite(siteKey)}
                aria-label={`Remove connection ${connectionName}`}
              >
                <X size={16} />
              </button>
            </div>
          </div>
        );
      })}
      <ContentDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedSite(null);
          setSelectedConnectionName(null);
          setSelectedConnectionId(null);
          setSelectedContentPaths([]);
        }}
        sitePath={selectedSite || ''}
        connectionName={selectedConnectionName || ''}
        connectionId={selectedConnectionId || ''}
        contentPaths={selectedContentPaths}
      />
    </div>
  );
};

const KnowledgeBaseForm: React.FC = () => {
  const [formData, setFormData] = useState<NewKnowledgeBaseForm>({
    name: '',
    description: '',
    locale: DEFAULT_LOCALE,
    knowledgeSourceList: [],
    contentSearch: false,
    showSharePointBrowser: false,
    selectedContentBySite: {},
    lastSync: new Date().toISOString(),
    totalArticlesCount: 0,
  });
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseItem[]>([]);
  const [connections, setConnections] = useState<SharePointConnection[]>([]);
  const [usageData, setUsageData] = useState<any>({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [kbCount, setKbCount] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [kbToDelete, setKbToDelete] = useState<KnowledgeBaseItem | null>(null);
  const [kbToEdit, setKbToEdit] = useState<KnowledgeBaseItem | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<FormValidationErrors>({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [changes, setChanges] = useState<{
    addedContent: Record<string, string[]>;
    removedContent: Record<string, string[]>;
    unchangedContent: Record<string, string[]>;
  }>({ addedContent: {}, removedContent: {}, unchangedContent: {} });

  const { executeRequest } = useApiRequest();
  const { fetchKnowledgeBases, fetchConnections, createOrUpdateKnowledgeBase, deleteKnowledgeBase, triggerKnowledgeBaseScan, getClientTrialUsageDetails, getKnowledgeBaseCount } = useKnowledgeBaseApi();

  const validateForm = (formData: NewKnowledgeBaseForm): FormValidationErrors => {
    const errors: FormValidationErrors = {};
    if (!formData.name.trim()) {
      errors.name = 'Knowledge base name is required';
    } else if (formData.name.length < 3 || formData.name.length > 30) {
      errors.name = 'Knowledge base name must be 3-30 characters long';
    }
    if (!Object.keys(formData.selectedContentBySite).length) {
      errors.content = 'At least one content item must be selected';
    }
    return errors;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [kbData, connData, usageData, allKbsCount ] = await Promise.all([
          executeRequest(
            () => fetchKnowledgeBases(),
            (data) => data,
            (error) => { throw error; }
          ),
          executeRequest(
            () => fetchConnections(),
            (data) => data,
            (error) => { throw error; }
          ),
          executeRequest(
            () => getClientTrialUsageDetails(),
            (data) => data,
            (error) => { throw error; }
          ),
          executeRequest(
            () => getKnowledgeBaseCount(),
            (data) => data?.length || 0,
            (error) => { throw error; }
          )
        ]);
        console.log("all kbs count", allKbsCount);
        setKnowledgeBases(kbData || []);
        setConnections(connData || []);
        setUsageData(usageData || []);
        setKbCount(allKbsCount || 0)
      } catch (error: any) {
        toast.error(`Error loading data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [executeRequest, fetchKnowledgeBases, fetchConnections, getClientTrialUsageDetails, getKnowledgeBaseCount]);

  const onhandleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setValidationErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const handleToggleContentSearch = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      contentSearch: !prev.contentSearch,
    }));
  }, []);

  const handleToggleSharePointBrowser = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      showSharePointBrowser: !prev.showSharePointBrowser,
    }));
    console.info('Toggled SharePoint content browser', { showSharePointBrowser: !formData.showSharePointBrowser });
  }, [formData.showSharePointBrowser]);

  const computeChanges = useCallback((currentContent: Record<string, string[]>, originalContent: Record<string, string[]>) => {
    const addedContent: Record<string, string[]> = {};
    const removedContent: Record<string, string[]> = {};
    const unchangedContent: Record<string, string[]> = {};
    
    const normalizePath = (path: string) => path.trim().toLowerCase().replace(/\/+$/, '');
    
    Object.entries(currentContent).forEach(([siteKey, currentPaths]) => {
      const originalPaths = (originalContent[siteKey] || []).map(normalizePath);
      
      const added = currentPaths.filter(path => !originalPaths.includes(normalizePath(path)));
      const unchanged = currentPaths.filter(path => originalPaths.includes(normalizePath(path)));
      
      if (added.length > 0) {
        addedContent[siteKey] = added;
      }
      if (unchanged.length > 0) {
        unchangedContent[siteKey] = unchanged;
      }
    });
    
    Object.entries(originalContent).forEach(([siteKey, originalPaths]) => {
      const currentPaths = (currentContent[siteKey] || []).map(normalizePath);
      const removed = originalPaths.filter(path => !currentPaths.includes(normalizePath(path)));
      
      if (removed.length > 0) {
        removedContent[siteKey] = removed;
      }
    });
    
    return { addedContent, removedContent, unchangedContent };
  }, []);

  const submitKnowledgeBase = useCallback(
    async (data: NewKnowledgeBaseForm, isEditMode: boolean) => {
      const errors = validateForm(data);
      if (Object.keys(errors).length) {
        setValidationErrors(errors);
        return false;
      }

      setIsSaving(true);
      try {
        const knowledgeBaseData = {
          knowledgeBaseId: data.id,
          knowledgeBaseName: data.name,
          knowledgeBaseDesc: data.description,
          locale: data.locale,
          contentSearch: data.contentSearch,
          knowledgeSourceList: Object.entries(data.selectedContentBySite).map(([siteKey, articlePaths]) => {
            const [sitePath, connectionId] = siteKey.split('||');
            const connection = connections.find((conn) => conn.connectionId === connectionId);
            return {
              sitePath,
              connectionId,
              connectionName: connection ? connection.connectionName : sitePath,
              articlePaths,
            };
          }),
          lastSync: isEditMode ? data?.lastSync : new Date().toISOString(),
          totalArticlesCount: isEditMode ? data?.totalArticlesCount || 0 : 0,
        };

        await executeRequest(
          () => createOrUpdateKnowledgeBase(knowledgeBaseData, isEditMode),
          () => {
            toast.success(isEditMode ? 'Knowledge base updated!' : 'Knowledge base created!');
            setShowCreateForm(false);
            setFormData({
              name: '',
              description: '',
              locale: DEFAULT_LOCALE,
              knowledgeSourceList: [],
              contentSearch: false,
              showSharePointBrowser: false,
              selectedContentBySite: {},
            });
            executeRequest(
              () => fetchKnowledgeBases(),
              (data) => setKnowledgeBases(data),
              (error) => toast.error(`Error loading knowledge bases: ${error.message}`)
            );
          },
          (error:any) => toast.error(`Error ${isEditMode ? 'updating' : 'creating'} knowledge base: ${error.data.message}`)
        );
        return true;
      } catch (error) {
        console.error('Error submitting form', error);
        return false;
      } finally {
        setIsSaving(false);
        setIsEditing(false);
        setKbToEdit(null);
      }
    },
    [executeRequest, createOrUpdateKnowledgeBase, fetchKnowledgeBases, connections]
  );

  const handleSubmit = useCallback(async () => {
    if (isEditing) {
      const originalContent = knowledgeBases.find(kb => kb.knowledgeBaseId === kbToEdit?.knowledgeBaseId)?.knowledgeSourceList.reduce<Record<string, string[]>>((acc, source) => {
        const compositeKey = `${source.sitePath}||${source.connectionId}`;
        return {
          ...acc,
          [compositeKey]: source.articlePaths
        };
      }, {}) || {};
      const currentSelectedContent: Record<string, string[]> = Object.entries(formData.selectedContentBySite).reduce((acc, [siteKey, articlePaths]) => {
        const [sitePath, connectionId] = siteKey.split('||');
        const connection = connections.find((conn) => conn.connectionId === connectionId);
        const key = connection ? `${sitePath}||${connectionId}` : siteKey;
        return {
          ...acc,
          [key]: articlePaths,
        };
      }, {});
      const changes = computeChanges(currentSelectedContent, originalContent);
      if (Object.keys(changes.addedContent).length > 0 || Object.keys(changes.removedContent).length > 0) {
        setChanges(changes);
        setShowPreviewModal(true);
        return;
      }
    }
    await submitKnowledgeBase(formData, isEditing);
  }, [formData, isEditing, submitKnowledgeBase, connections, kbToEdit, knowledgeBases, computeChanges]);

  const handleEdit = useCallback(
    (knowledgeBaseId: string) => {
      const kb = knowledgeBases.find((kb) => kb.knowledgeBaseId === knowledgeBaseId);
      if (!kb) {
        toast.error('Knowledge base not found');
        return;
      }
      setKbToEdit(kb);
      const selectedContentBySite = kb.knowledgeSourceList.reduce<Record<string, string[]>>(
        (acc, source) => {
          const compositeKey = `${source.sitePath}||${source.connectionId}`;
          return {
            ...acc,
            [compositeKey]: source.articlePaths,
          };
        },
        {}
      );

      setFormData({
        id: kb.knowledgeBaseId,
        name: kb.knowledgeBaseName,
        description: kb.knowledgeBaseDesc,
        locale: kb.locale || DEFAULT_LOCALE,
        knowledgeSourceList: kb.knowledgeSourceList,
        contentSearch: kb.contentSearch || false,
        showSharePointBrowser: false,
        selectedContentBySite,
        lastSync: kb.lastSync,
        totalArticlesCount: kb.totalArticlesCount,
      });
      setIsEditing(true);
      setShowCreateForm(true);
      setValidationErrors({});
    },
    [knowledgeBases]
  );

  const triggerAdhocScan = useCallback(async (knowledgeBase: any) => {
    setIsSyncing((prev) => [...prev, knowledgeBase.knowledgeBaseId]);
    try {
      await executeRequest(
        () => triggerKnowledgeBaseScan(knowledgeBase),
        () => {
          toast.success('Knowledge base sync started successfully!');
        },
        (error) => toast.error(`Error starting sync for knowledge base: ${error.message}`)
      );
    } finally {
      setIsSyncing((prev) => prev.filter(id => id !== knowledgeBase.knowledgeBaseId));
    }
  }, [executeRequest, triggerKnowledgeBaseScan]);

  const handleCancel = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      locale: DEFAULT_LOCALE,
      knowledgeSourceList: [],
      contentSearch: false,
      showSharePointBrowser: false,
      selectedContentBySite: {},
    });
    setIsEditing(false);
    setShowCreateForm(false);
    setValidationErrors({});
    setShowPreviewModal(false);
  }, []);

  const openKBCreateForm = useCallback(() => {
    if (showCreateForm) {
      handleCancel();
      return;
    }

    if (kbCount >= MAX_KNOWLEDGE_BASES) {
      toast.error('You have reached the maximum limit of 500 knowledge bases.');
      return;
    }

    setShowCreateForm(true);
    setIsEditing(false);
  }, [showCreateForm, handleCancel, kbCount]);

  const openDeleteModal = useCallback((knowledgeBase: any) => {
    setKbToDelete(knowledgeBase);
    setIsDeleteModalOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!kbToDelete) return;

    setIsDeleting(true);
    try {
      await executeRequest(
        () => deleteKnowledgeBase(kbToDelete),
        () => {
          setIsDeleteModalOpen(false);
          setKbToDelete(null);
          executeRequest(
            () => fetchKnowledgeBases(),
            (data) => setKnowledgeBases(data),
            (error) => toast.error(`Error loading knowledge bases: ${error.message}`)
          );
          toast.success('Knowledge base deleted successfully!');
        },
        (error) => toast.error(`Error deleting knowledge base: ${error.message}`)
      );
    } catch (err: any) {
      console.error('Failed to delete knowledge base', err);
    } finally {
      setIsDeleting(false);
    }
  }, [kbToDelete, executeRequest, deleteKnowledgeBase, fetchKnowledgeBases]);

  const handleContentSelect = useCallback(
    (compositeKey: string, contentPath: string) => {
      setFormData((prev) => {
        const siteContent = prev.selectedContentBySite[compositeKey] || [];
        let newSiteContent: string[];
        if (contentPath === '') {
          newSiteContent = [];
        } else {
          newSiteContent = siteContent.includes(contentPath)
            ? siteContent.filter((path) => path !== contentPath)
            : [...siteContent, contentPath];
        }
        
        const newSelectedContentBySite = {
          ...prev.selectedContentBySite,
          [compositeKey]: newSiteContent,
        };

        if (newSiteContent.length === 0) {
          const { [compositeKey]: _, ...rest } = newSelectedContentBySite;
          return {
            ...prev,
            selectedContentBySite: rest,
          };
        }

        return {
          ...prev,
          selectedContentBySite: newSelectedContentBySite,
        };
      });
      setValidationErrors((prev) => ({ ...prev, content: undefined }));
    },
    []
  );

  const onRemoveSite = useCallback((compositeKey: string) => {
    setFormData((prev) => {
      if (compositeKey === 'all') {
        return { ...prev, selectedContentBySite: {} };
      }
      const { [compositeKey]: _, ...rest } = prev.selectedContentBySite;
      return { ...prev, selectedContentBySite: rest };
    });
  }, []);

  const originalContentBySite = useMemo<Record<string, string[]>>(() => {
    if (!isEditing) return {};
    const kb = knowledgeBases.find((kb) => kb.knowledgeBaseId === kbToEdit?.knowledgeBaseId);
    if (!kb) return {};
    return kb.knowledgeSourceList.reduce<Record<string, string[]>>((acc, source) => {
      const compositeKey = `${source.sitePath}||${source.connectionId}`;
      return {
        ...acc,
        [compositeKey]: source.articlePaths,
      };
    }, {});
  }, [isEditing, knowledgeBases, kbToEdit]);

  return (
    <div className="pt-16 ml-64">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Knowledge Bases</h2>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={openKBCreateForm}
            disabled={loading || kbCount >= MAX_KNOWLEDGE_BASES}
            aria-label={showCreateForm ? 'Cancel creating knowledge base' : 'Create new knowledge base'}
          >
            {showCreateForm ? (
              'Cancel'
            ) : (
              <>
                <Plus size={16} className="mr-2" />
                Add Knowledge Base
              </>
            )}
          </button>
        </div>
        {loading && (
          <LoadingSpinner
            title="Loading Knowledge Bases"
            message="Fetching your knowledge base configurations and SharePoint connections..."
            size="large"
            fullScreen={false}
            className="py-16"
          />
        )}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-lg font-medium mb-4">{isEditing ? 'Edit Knowledge Base' : 'Create New Knowledge Base'}</h3>
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label htmlFor="kb-name" className="block text-sm font-medium text-gray-700">
                  Knowledge Base Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="kb-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={onhandleChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${validationErrors.name ? 'border-red-500' : ''}`}
                  placeholder="e.g., Technical Documentation"
                  required
                  aria-invalid={!!validationErrors.name}
                  aria-describedby={validationErrors.name ? 'name-error' : ''}
                />
                {validationErrors.name && (
                  <p id="name-error" className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>
              <div>
                <label htmlFor="kb-description" className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  id="kb-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={onhandleChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm`}
                  placeholder="Enter a description of this knowledge base"
                />
                {validationErrors.description && (
                  <p id="description-error" className="mt-1 text-sm text-red-600">{validationErrors.description}</p>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="kb-locale" className="block text-sm font-medium text-gray-700">Language</label>
                  <select
                    id="kb-locale"
                    name="locale"
                    value={formData.locale}
                    onChange={onhandleChange}
                    disabled={isEditing}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  >
                    {LOCALES.map(locale => (
                      <option key={locale.value} value={locale.value}>
                        {locale.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <Toggler
                    checked={formData.contentSearch}
                    onChange={handleToggleContentSearch}
                    label="Enable Content Search"
                    ariaLabel="Enable content search"
                    disabled={isEditing}
                  />
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-medium">SharePoint Content</h4>
                <button
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  onClick={handleToggleSharePointBrowser}
                  aria-label={'Browse'}
                >
                  <>
                    <FileInput size={16} className="mr-2" />
                    Browse
                  </>
                </button>
              </div>

              {formData.selectedContentBySite && Object.keys(formData.selectedContentBySite).length > 0 && (
                <SelectedContentSummary
                  selectedContentBySite={formData.selectedContentBySite}
                  onRemoveSite={onRemoveSite}
                  isEditing={isEditing}
                  connections={connections}
                  originalContentBySite={originalContentBySite}
                />
              )}
              {validationErrors.content && (
                <p className="text-sm text-red-600 mb-4">{validationErrors.content}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Cancel form"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={isEditing ? 'Update knowledge base' : 'Save knowledge base'}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isEditing ? 'Updating...' : 'Saving...'}
                  </>
                ) : isEditing ? (
                  <>
                    <Pencil size={16} className="mr-2" />
                    Update Knowledge Base
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Save Knowledge Base
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        {!loading && !showCreateForm && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Articles
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Sync
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {knowledgeBases.map((kb) => (
                  <tr key={kb.knowledgeBaseId}>
                    <td className="px-4 py-6 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{kb.knowledgeBaseName}</div>
                    </td>
                    <td className="px-4 py-6 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{kb.knowledgeBaseDesc?.length > 50 ? kb.knowledgeBaseDesc.substring(0, 50) + '...' : kb.knowledgeBaseDesc}</div>
                    </td>
                    <td className="px-4 py-6 whitespace-nowrap">
                      <div className="text-sm ml-4">{kb.totalArticlesCount ? kb.totalArticlesCount : 0}</div>
                    </td>
                    <td className="px-4 py-6 whitespace-nowrap text-gray-500">
                      <div className="text-sm">{formatDateTime(kb.lastSync)}</div>
                    </td>
                    <td className="px-4 py-6 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {kb.status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-3">
                        <button
                          className="text-green-600 hover:text-green-800 transition-colors"
                          onClick={() => handleEdit(kb.knowledgeBaseId)}
                          aria-label={`Edit ${kb.knowledgeBaseName}`}
                          title="Edit knowledge base"
                        >
                          <Pencil size={16} className="inline mr-1" />
                        </button>
                        {usageData?.subscription_status === 'TRIAL' ? 
                        usageData?.kb_sync_count < 5 && (
                        <button
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          onClick={() => triggerAdhocScan(kb)}
                          disabled={isSyncing.includes(kb.knowledgeBaseId)}
                          aria-label={`Sync ${kb.knowledgeBaseName}`}
                          title="Sync now"
                        >
                          {isSyncing.includes(kb.knowledgeBaseId) ? (
                            <>
                              <Loader size={16} className="inline mr-1 animate-spin" />
                              Syncing...
                            </>
                          ) : (
                            <RefreshCw size={16} className="inline mr-1" />
                          )}
                        </button>
                        )
                        :
                        (
                          <button
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          onClick={() => triggerAdhocScan(kb)}
                          disabled={isSyncing.includes(kb.knowledgeBaseId)}
                          aria-label={`Sync ${kb.knowledgeBaseName}`}
                          title="Sync now"
                        >
                          {isSyncing.includes(kb.knowledgeBaseId) ? (
                            <>
                              <Loader size={16} className="inline mr-1 animate-spin" />
                              Syncing...
                            </>
                          ) : (
                            <RefreshCw size={16} className="inline mr-1" />
                          )}
                        </button>
                        )}
                        <button
                          className="text-red-600 hover:text-red-800 transition-colors"
                          onClick={() => openDeleteModal(kb)}
                          disabled={isDeleting && kbToDelete?.knowledgeBaseId === kb.knowledgeBaseId}
                          aria-label={`Delete ${kb.knowledgeBaseName}`}
                          title="Delete knowledge base"
                        >
                          {isDeleting && kbToDelete?.knowledgeBaseId === kb.knowledgeBaseId ? (
                            <>
                              <Loader size={16} className="inline mr-1 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            <Trash2 size={16} className="inline mr-1" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {knowledgeBases.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                      No knowledge bases found. Create your first knowledge base to start importing content.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setKbToDelete(null);
        }}
        onConfirm={confirmDelete}
        knowledgeBaseName={kbToDelete?.knowledgeBaseName || ''}
        isDeleting={isDeleting}
      />
      <PreviewChangesModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        connections={connections}
        onConfirm={async () => {
          setShowPreviewModal(false);
          await submitKnowledgeBase(formData, isEditing);
        }}
        addedContent={changes.addedContent}
        removedContent={changes.removedContent}
        unchangedContent={changes.unchangedContent}
      />
      <SharePointBrowserModal
        isOpen={formData.showSharePointBrowser}
        onClose={handleToggleSharePointBrowser}
        connections={connections}
        selectedContentBySite={formData.selectedContentBySite}
        onContentSelect={handleContentSelect}
      />
    </div>
  );
};

export default KnowledgeBaseForm;